import { Storage } from 'aws-amplify'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useIndexedDB } from 'react-indexed-db-hook'
import { useNetworkState } from 'react-use'
import { getUpdateStatus, updateCustomerKitMaterial } from 'service/updateCustomerKitMaterial'
import { useSnackbar } from 'notistack'
import { createReport as createCXReport, getReportPayloadURL, getReportStatus } from 'service/cxReport'
import { saveAs } from 'file-saver'
import { Button } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import { useQuery } from '@tanstack/react-query'

const BackgroundProcessContext = createContext({
  addProcess: async () => {}
})

export const FILE_UPLOAD_DB_NAME = 'file-upload'

export const useBackgroundProcess = () => useContext(BackgroundProcessContext)

const makeStatusRequest = async (type, id) => {
  if (type === 'report') {
    return getReportStatus(id)
  } else if (type === 'Kit-Material') {
    return getUpdateStatus(id)
  }
}

export const BackgroundProcessContextProvider = ({ children }) => {
  const { add, deleteRecord, getAll, update } = useIndexedDB(FILE_UPLOAD_DB_NAME)
  const { online } = useNetworkState()

  const [requestParams, setRequestParams] = useState({})

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const addMessage = useCallback((message, type, duration, persist) => {
    return enqueueSnackbar({
      message,
      variant: type,
      ...(persist
        ? { persist }
        : {
            autoHideDuration: duration ?? 3000
          })
    })
  }, [])

  const processFileupload = useCallback(async () => {
    const uploads = await getAll()
    const promises = []

    const uploadImage = async (upload) => {
      try {
        await Storage.put(upload.path, upload.blob, {
          metadata: { original: 'true' }
        })
        await deleteRecord(upload.id)
      } catch (e) {
        if (upload.retries < 3) {
          update(upload + 1, 'retries').then(() => {
            // nothing
          })
        }
      }
    }

    for (const upload of uploads) {
      if (upload.retries <= 3) {
        promises.push(uploadImage(upload))
      }
    }

    if (promises.length > 0) {
      await Promise.allSettled(promises)
      addMessage('Your file upload has been successfully completed')
    }
  }, [deleteRecord, getAll])

  useEffect(() => {
    if (!online) {
      return
    }
    processFileupload().catch(() => {
      addMessage('Your photo upload failed, it would be tried again', 'error')
    })
  }, [online, processFileupload])

  const updateKitMaterial = useCallback(
    async (processId, customerId, materialNumber, workingDepth) => {
      try {
        addMessage(`Updating Working Depth of '${materialNumber}' to '${workingDepth}'`, 'info')
        const response = await updateCustomerKitMaterial(customerId, materialNumber, workingDepth)
        if (response.status !== 201) {
          addMessage(`An error occurred while updating Kit material number`, 'error')
          return
        }
        addMessage("Your request is being processed. You will be notified of it's status when available", 'info')
        return response.data?.id
      } catch (e) {
        if (e.response?.data?.message) {
          addMessage(`An error occurred while updating Kit material number.`, 'error')
        } else {
          addMessage(`An unknown error occurred while updating Kit material number.`, 'error')
        }
      }
    },
    [addMessage]
  )

  const createReport = useCallback(
    async (processId, customerId, reportType, data) => {
      try {
        addMessage('Creating report', 'info')

        const payloadResponse = await getReportPayloadURL(customerId, reportType)
        if (payloadResponse.status !== 201) {
          addMessage(`An error occurred while creating report.`, 'error')
          return
        }
        const response = await createCXReport(
          payloadResponse.data.url,
          payloadResponse.data.id,
          customerId,
          reportType,
          data
        )
        if (response.status !== 200) {
          addMessage(`An error occurred while creating report.`, 'error')
          return
        }
        addMessage("Your request is being processed. You will be notified of its status when available. Until then, you can't create another report.", 'info')

        // addMessage("Your request is being processed. You will be notified of it's status when available", 'info')
        return payloadResponse.data?.id
      } catch (e) {
        if (e.response?.data?.message) {
          addMessage(`An error occurred while creating report.`, 'error')
        } else {
          addMessage(`An unknown error occurred while creating report.`, 'error')
        }
      }
    },
    [addMessage]
  )
  const fetchRequestStatus = useCallback(async () => {
    const { type, response } = requestParams

    const {
      data: { item, downloadUrl }
    } = await makeStatusRequest(type, response)

    if (!['queued', 'processing'].includes(item?.status)) {
      if (type === 'Kit-Material') {
        addMessage(
          `Your request to update Working Depth of '${process.materialNumber}' to '${process.workingDepth}' has ${
            item?.status === 'processed'
              ? 'been completed successful. Please note that this update would be reflected on your next sync.'
              : 'failed'
          }`,
          item?.status === 'processed' ? 'success' : 'error',
          item?.status === 'processed' ? 30 * 1000 : 6 * 1000
        )
      } else {
        const message = `Your report request has ${item?.status === 'processed' ? 'been completed' : 'failed'}`
        const id = addMessage(
          <span>
            {message}
            {item.status === 'processed' && (
              <Button
                onClick={() => {
                  saveAs(downloadUrl)
                  closeSnackbar(id)
                }}>
                click here to download it
              </Button>
            )}
          </span>,
          item?.status === 'processed' ? 'success' : 'error',
          item?.status === 'processed' ? undefined : 6 * 1000,
          item.status === 'processed' ? true : undefined
        )
      }

      if (type === 'report' && item?.status === 'processed') {
        saveAs(downloadUrl)
      }

      return item
    }
    throw new Error('Request is still processing')
  }, [addMessage, requestParams])

  const results = useQuery({
    queryKey: ['fetchRequestStatus', requestParams?.type, requestParams?.response],
    queryFn: fetchRequestStatus,
    retry: 8,
    enabled: Object.keys(requestParams).length > 0,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 2 * 60_000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false
  })

  useEffect(() => {
    if (results.error && requestParams?.type === 'report') {
      addMessage('Report generation has timedout. Please contact support')
    }
  }, [results.error, addMessage])

  const addProcess = useCallback(
    async (process) => {
      const id = uuidv4()
      switch (process.type) {
        case 'Kit-Material': {
          const response = await updateKitMaterial(id, process.customerId, process.materialNumber, process.workingDepth)
          if (response) {
            setRequestParams({
              id,
              type: process.type,
              response
            })
          }
          break
        }
        case FILE_UPLOAD_DB_NAME: {
          add({
            path: process.path,
            blob: process.file,
            retries: 0
          }).then(() => {
            addMessage('Your photo upload has been queued', 'info')
          })
          break
        }
        case 'report': {
          const response = await createReport(id, process.customerId, process.reportType, process.data)
          if (response) {
            setRequestParams({
              id,
              type: process.type,
              response
            })
          }
          break
        }
      }
    },
    [updateKitMaterial, add]
  )

  return (
    <BackgroundProcessContext.Provider
      value={{
        addProcess
      }}>
      {children}
    </BackgroundProcessContext.Provider>
  )
}
